import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Anchor, Ban, CalendarX, Info, Trash2 } from "lucide-react";
import Seo from "../components/Seo";
import CartLineImage from "../components/CartLineImage";
import PendingPaymentBanner from "../components/PendingPaymentBanner";
import { useTrip } from "../hooks/useTrip";
import { postQuote } from "../lib/api";
import { money, prettyDate, time12 } from "../lib/format";
import { LOCATIONS } from "../lib/content";
import { toVendorLines, addonTotal } from "../lib/lines";
import { listPendingOrders, PENDING_EVENT } from "../lib/pendingOrders";
import { EXPIRED, PRICE_CHANGED, PRICING_UNKNOWN, SELF_CONFLICT, UNPRICEABLE, classifyLine, hasDeparted, isBlocking, vendorLineIndex } from "../lib/cartHealth";

const WB = LOCATIONS["west-branch"];

/** One order = one platform LocationId, so the cart's location comes from the
 *  lines themselves. Falls back to West Branch only for an empty cart. */
const cartLocationId = (lines) => lines.find((l) => l.locationId)?.locationId ?? WB.locationId;

export default function Cart() {
  const { lines, removeLine } = useTrip();
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState(null);
  const [accepted, setAccepted] = useState({});
  const [pending, setPending] = useState(listPendingOrders);

  useEffect(() => {
    const sync = () => setPending(listPendingOrders());
    window.addEventListener(PENDING_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(PENDING_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  // Lines whose departure has already gone are EXPIRED before any network call:
  // a cart persisted in localStorage can be days old, and the vendor prices a
  // past departure at 0.00 rather than refusing it.
  const liveIndexes = useMemo(
    () => lines.map((l, i) => (hasDeparted(l) ? null : i)).filter((i) => i !== null),
    [lines],
  );
  const liveLines = liveIndexes.map((i) => lines[i]);

  useEffect(() => {
    if (!liveLines.length) {
      setQuote(null);
      setQuoteError(null);
      return undefined;
    }
    let alive = true;
    setQuoteError(null);
    // Only the lines that can legitimately be priced are ever sent upstream.
    postQuote(cartLocationId(liveLines), toVendorLines(liveLines))
      .then((q) => alive && setQuote(q))
      .catch((err) => {
        if (!alive) return;
        setQuote(null);
        // The payload's own code decides whether this condemns a line or is just
        // a wobble: a 429 says nothing about the boat.
        setQuoteError(err.payload || { code: "network" });
      });
    return () => {
      alive = false;
    };
  }, [JSON.stringify(liveLines)]);

  // Per-line classification. Distinct outcomes, never collapsed into one vague
  // "pricing or availability changed" banner.
  const health = lines.map((line, i) => {
    const sent = liveIndexes.indexOf(i);
    const ql = sent >= 0 && quote?.lines ? quote.lines[vendorLineIndex(liveLines, sent)] : null;
    return classifyLine(line, ql, { quoteError: sent >= 0 ? quoteError : null, pending });
  });

  const blocked = health.filter((h) => isBlocking(h.state));
  const unacceptedChanges = health.filter((h, i) => h.state === PRICE_CHANGED && !accepted[lines[i].id]);
  const checkoutAllowed = lines.length > 0 && blocked.length === 0 && unacceptedChanges.length === 0;

  // The total shown must never quietly omit a line that is on screen. Excluded
  // lines are called out on the line itself AND in the summary.
  const excludedNames = lines.filter((l, i) => isBlocking(health[i].state)).map((l) => l.displayName);

  return (
    <>
      <Seo
        title="Your Trip | West Branch Marina"
        description="Review the boats in your trip, their dates, durations and departure times before you book."
        path="/cart"
      />

      <div className="bg-navy pt-24 pb-10 lg:pt-32 lg:pb-14">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <p className="eyebrow text-lake">Your trip</p>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-white">
            {lines.length === 0 ? "Nothing in your trip yet" : `${lines.length} boat${lines.length === 1 ? "" : "s"} ready to go`}
          </h1>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-5 py-12 lg:px-10 lg:py-16">
        <PendingPaymentBanner className="mb-8" />
        {lines.length === 0 ? (
          <div data-testid="cart-empty-state" className="rounded-3xl border border-sand-border bg-white p-10 text-center">
            <Anchor className="mx-auto h-9 w-9 text-lake" aria-hidden="true" />
            <p className="mt-4 font-display text-2xl font-bold text-navy">Your trip is empty</p>
            <p className="mx-auto mt-3 max-w-md text-sm text-slate-600">
              Pick a date and add a boat — you can combine several boats in one trip.
            </p>
            <Link
              to="/boat-rentals/west-branch"
              data-testid="cart-browse-fleet-link"
              className="mt-7 inline-flex items-center gap-2 rounded-full bg-ember px-7 py-3.5 font-bold text-white transition-colors hover:bg-ember-hover"
            >
              Browse the fleet
            </Link>
          </div>
        ) : (
          <div className="grid gap-10 lg:grid-cols-[1.6fr_1fr] lg:gap-14">
            <div data-testid="cart-lines" className="space-y-5">
              {blocked.length > 0 && (
                <div
                  data-testid="cart-blocking-warning"
                  role="alert"
                  className="flex items-start gap-3 rounded-xl border border-red-300 bg-red-50 p-4"
                >
                  <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-700" aria-hidden="true" />
                  <p className="text-sm text-red-900">
                    {blocked.length === 1
                      ? "One boat in your trip can't be booked as it stands"
                      : `${blocked.length} boats in your trip can't be booked as they stand`}{" "}                    — {blocked.map((b) => b.title.toLowerCase()).join("; ")}. Edit or remove{" "}
                    {blocked.length === 1 ? "it" : "them"} to carry on. Everything else is still held.
                  </p>
                </div>
              )}

              {lines.map((l, i) => {
                const h = health[i];
                const excluded = isBlocking(h.state);
                const sent = liveIndexes.indexOf(i);
                const ql = sent >= 0 && quote?.lines ? quote.lines[vendorLineIndex(liveLines, sent)] : null;
                return (
                  <div
                    key={l.id}
                    data-testid={`cart-line-${l.id}`}
                    data-line-state={h.state}
                    className={`flex flex-col gap-5 rounded-3xl border bg-white p-5 sm:flex-row ${
                      excluded ? "border-red-300" : "border-sand-border"
                    }`}
                  >
                    <div className={`w-full shrink-0 overflow-hidden rounded-2xl sm:w-44 ${excluded ? "opacity-60" : ""}`}>
                      <CartLineImage line={l} locationId={l.locationId ?? WB.locationId} />
                    </div>

                    <div className="flex-1">
                      <p className="eyebrow text-lake">{l.assetTypeName}</p>
                      <h2 className="mt-1 font-display text-xl font-bold text-navy">{l.displayName}</h2>

                      {h.state !== "ok" && (
                        <div
                          data-testid={`cart-line-status-${l.id}`}
                          role="status"
                          className={`mt-3 flex items-start gap-2 rounded-xl border p-3 text-sm ${
                            h.state === PRICING_UNKNOWN
                              ? "border-slate-300 bg-sand-card text-slate-700"
                              : excluded
                                ? "border-red-300 bg-red-50 text-red-900"
                                : "border-amber-300 bg-amber-50 text-amber-900"
                          }`}
                        >
                          {h.state === EXPIRED ? (
                            <CalendarX className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                          ) : h.state === UNPRICEABLE || h.state === SELF_CONFLICT ? (
                            <Ban className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                          ) : h.state === PRICING_UNKNOWN ? (
                            <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                          ) : (
                            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                          )}
                          <div>
                            <p className="font-bold">{h.title}</p>
                            <p className="mt-0.5">{h.detail}</p>
                            {h.state === SELF_CONFLICT && h.pendingOrder && (
                              <Link
                                to={`/booking/payment?code=${encodeURIComponent(h.pendingOrder.code)}`}
                                data-testid={`cart-line-self-conflict-link-${l.id}`}
                                className="mt-2 inline-block font-bold underline"
                              >
                                See your pending reservation {h.pendingOrder.code}
                              </Link>
                            )}
                            {h.state === PRICE_CHANGED && (
                              <p data-testid={`cart-line-price-change-${l.id}`} className="mt-1">
                                Was <s>{money(h.oldAmount)}</s>, now{" "}
                                <strong className="font-bold">{money(h.newAmount)}</strong>.
                              </p>
                            )}
                            {excluded && (
                              <p data-testid={`cart-line-excluded-${l.id}`} className="mt-1 font-bold">
                                Not included in your trip total.
                              </p>
                            )}
                            {h.state === PRICE_CHANGED && !accepted[l.id] && (
                              <button
                                type="button"
                                data-testid={`cart-line-accept-price-${l.id}`}
                                onClick={() => setAccepted((a) => ({ ...a, [l.id]: true }))}
                                className="mt-3 rounded-full bg-navy px-4 py-2 text-xs font-bold text-white hover:bg-navy/90"
                              >
                                Accept the new price
                              </button>
                            )}
                            {h.state === PRICE_CHANGED && accepted[l.id] && (
                              <p data-testid={`cart-line-price-accepted-${l.id}`} className="mt-2 font-bold">
                                New price accepted.
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
                        <div>
                          <dt className="text-xs font-bold uppercase tracking-wider text-slate-500">Date</dt>
                          <dd data-testid={`cart-line-date-${l.id}`} className="text-slate-700">
                            {prettyDate(l.date)}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs font-bold uppercase tracking-wider text-slate-500">Duration</dt>
                          <dd className="text-slate-700">{l.tierLabel}</dd>
                        </div>
                        <div>
                          <dt className="text-xs font-bold uppercase tracking-wider text-slate-500">Times</dt>
                          <dd data-testid={`cart-line-times-${l.id}`} className="text-slate-700">
                            {time12(l.startTime)} – {time12(l.endTime)}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs font-bold uppercase tracking-wider text-slate-500">Vessels</dt>
                          <dd className="text-slate-700">{l.quantity}</dd>
                        </div>
                        {(l.addons || []).length > 0 && (
                          <div>
                            <dt className="text-xs font-bold uppercase tracking-wider text-slate-500">Add-ons</dt>
                            <dd data-testid={`cart-line-addons-${l.id}`} className="text-slate-700">
                              {l.addons.map((a) => `${a.label}${a.quantity > 1 ? ` ×${a.quantity}` : ""}`).join(", ")}
                            </dd>
                          </div>
                        )}
                      </dl>

                      {ql && !excluded && (
                        <ul className="mt-4 space-y-1 text-sm">
                          <li className="flex justify-between gap-4">
                            <span className="text-slate-600">Rental</span>
                            <span className="font-semibold text-navy">{money(ql.baseAmount)}</span>
                          </li>
                          {addonTotal(l) > 0 && (
                            <li className="flex justify-between gap-4">
                              <span className="text-slate-600">Add-ons</span>
                              <span className="font-semibold text-navy">{money(addonTotal(l))}</span>
                            </li>
                          )}
                          {ql.taxAmount > 0 && (
                            <li className="flex justify-between gap-4">
                              <span className="text-slate-600">Sales tax</span>
                              <span className="font-semibold text-navy">{money(ql.taxAmount)}</span>
                            </li>
                          )}
                          <li className="flex justify-between gap-4 border-t border-sand-border pt-2">
                            <span className="font-bold text-navy">Line total</span>
                            <span data-testid={`cart-line-total-${l.id}`} className="font-display font-bold text-navy">
                              {money(ql.totalAmount + addonTotal(l))}
                            </span>
                          </li>
                        </ul>
                      )}

                      <div className="mt-5 flex flex-wrap items-center gap-3">
                        <Link
                          to={`/boat-rentals/west-branch/boats/${l.assetId}?date=${l.date}`}
                          data-testid={`cart-line-edit-${l.id}`}
                          className={`rounded-full px-4 py-2 text-sm font-bold transition-colors ${
                            excluded
                              ? "bg-navy text-white hover:bg-navy/90"
                              : "border border-navy/20 text-navy hover:bg-sand-card"
                          }`}
                        >
                          {excluded ? "Pick a new time" : "Edit"}
                        </Link>
                        <button
                          type="button"
                          data-testid={`cart-line-remove-${l.id}`}
                          onClick={() => removeLine(l.id)}
                          className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold transition-colors ${
                            excluded
                              ? "border border-red-300 text-red-700 hover:bg-red-50"
                              : "text-slate-600 hover:bg-sand-card hover:text-navy"
                          }`}
                        >
                          <Trash2 className="h-4 w-4" aria-hidden="true" />
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="lg:sticky lg:top-28 lg:self-start">
              <div data-testid="cart-summary" className="rounded-3xl border border-sand-border bg-white p-6 shadow-xl shadow-navy/5">
                <p className="eyebrow text-lake">Trip summary</p>
                {excludedNames.length > 0 && (
                  <p data-testid="cart-summary-exclusion-note" className="mt-3 rounded-xl border border-red-300 bg-red-50 p-3 text-xs font-bold text-red-900">
                    {excludedNames.join(", ")} {excludedNames.length === 1 ? "is" : "are"} not included in these
                    figures — {excludedNames.length === 1 ? "it needs" : "they need"} a new time or removing first.
                  </p>
                )}
                {quote ? (
                  <>
                    <dl className="mt-4 space-y-2.5 text-sm">
                      <div className="flex justify-between gap-4">
                        <dt className="text-slate-600">Boats &amp; gear</dt>
                        <dd data-testid="cart-subtotal" className="font-semibold text-navy">
                          {money(quote.orderBase)}
                        </dd>
                      </div>
                      {quote.orderFees > 0 && (
                        <div className="flex justify-between gap-4">
                          <dt className="text-slate-600">Fees</dt>
                          <dd data-testid="cart-extras" className="font-semibold text-navy">
                            {money(quote.orderFees)}
                          </dd>
                        </div>
                      )}
                      {quote.orderTax > 0 && (
                        <div className="flex justify-between gap-4">
                          <dt className="text-slate-600">
                            Sales tax{quote.taxRateLabel ? ` (${quote.taxRateLabel})` : ""}
                          </dt>
                          <dd data-testid="cart-tax" className="font-semibold text-navy">
                            {money(quote.orderTax)}
                          </dd>
                        </div>
                      )}
                      <div className="flex justify-between gap-4 border-t border-sand-border pt-4">
                        <dt className="font-display text-base font-bold text-navy">Total</dt>
                        <dd data-testid="cart-grand-total" className="font-display text-xl font-bold text-navy">
                          {money(quote.orderTotal)}
                        </dd>
                      </div>
                      {quote.hasDeposit && (
                        <>
                          <div className="flex justify-between gap-4">
                            <dt className="font-bold text-navy">Pay today</dt>
                            <dd data-testid="cart-pay-today" className="font-bold text-navy">
                              {money(quote.payTodayAmount)}
                            </dd>
                          </div>
                          <div className="flex justify-between gap-4">
                            <dt className="text-slate-600">Balance due at the marina</dt>
                            <dd data-testid="cart-balance" className="font-semibold text-navy">
                              {money(quote.balanceDueAmount)}
                            </dd>
                          </div>
                        </>
                      )}
                    </dl>
                    <p data-testid="cart-total-note" className="mt-3 flex items-start gap-2 text-xs text-slate-500">
                      <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                      {quote.hasDeposit
                        ? `You'll pay ${money(quote.payTodayAmount)} now to hold your boats, with ${money(quote.balanceDueAmount)} due when you check in.`
                        : "This is the full price, charged when you book."}
                    </p>
                  </>
                ) : (
                  <p data-testid="cart-quote-status" className="mt-4 text-sm text-slate-600">
                    {liveLines.length === 0
                      ? "None of these boats can be priced as they stand."
                      : quoteError
                        ? quoteError.userMessage || "We couldn't price this trip just now. Please try again in a moment."
                        : "Checking today's prices…"}
                  </p>
                )}

                {checkoutAllowed ? (
                  <Link
                    to="/checkout"
                    data-testid="cart-checkout-button"
                    className="mt-6 block w-full rounded-full bg-ember px-6 py-4 text-center font-bold text-white transition-colors hover:bg-ember-hover"
                  >
                    Continue to checkout
                  </Link>
                ) : (
                  <>
                    <button
                      type="button"
                      data-testid="cart-checkout-button"
                      disabled
                      aria-describedby="cart-checkout-blocked-reason"
                      className="mt-6 block w-full cursor-not-allowed rounded-full bg-slate-300 px-6 py-4 text-center font-bold text-white"
                    >
                      Continue to checkout
                    </button>
                    <p
                      id="cart-checkout-blocked-reason"
                      data-testid="cart-checkout-blocked-reason"
                      role="status"
                      className="mt-3 text-xs font-bold text-red-700"
                    >
                      {blocked.length > 0
                        ? `Remove or re-time ${excludedNames.join(", ")} to continue.`
                        : unacceptedChanges.length > 0
                          ? "Please accept the new price above to continue."
                          : "Checking today's prices…"}
                    </p>
                  </>
                )}

                <Link
                  to="/boat-rentals/west-branch"
                  data-testid="cart-add-another-link"
                  className="mt-3 block w-full rounded-full border border-navy/20 px-6 py-3 text-center text-sm font-bold text-navy transition-colors hover:bg-sand-card"
                >
                  Add another boat
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
